/**
 * The land's look (decision D1-look, master plan §10): the GDD's ≤ 1.4 m terrain mesh keeps the concept's land.
 *
 * The terrain functions carry detail repeating every 1.7–2 m (`pd()`). The concept's coarser grid samples it into
 * the soft, broad patches of the reference screens; a 1.4 m grid resolves it into regular diagonal streaks. So
 * the fine mesh follows the concept's own triangles inland: height, colour and normal are interpolated across the
 * concept grid's triangle under each fine vertex, exactly as the GPU interpolates them when drawing the concept's
 * mesh. At the waterline (exact height under 1 m) the fine mesh keeps the exact surface: crisp banks and
 * shorelines, the reason for the GDD's spacing. The two blend up to 2.2 m, where the sand band ends.
 *
 * The concept grid's rows are made to divide 800 m (2.5157 m instead of 2.5152 m for the classic grid), so the
 * land repeats exactly and the world shift stays seamless. Bank collisions use this same surface (`height`).
 */
import type { BufferGeometry } from 'three';
import { lerp, PERIOD, sstep } from '../core/math.ts';
import type { WorldProfile } from './profiles.ts';
import { buildTerrain } from './terrain.ts';

/** The concept terrain grid the land is sampled from: columns packed toward the river, rows spaced evenly. */
export interface Lattice {
  /** Columns across (the concept's nx). */
  nx: number;
  /** Row spacing along z (m), dividing 800 m. */
  spacing: number;
  /** Half-width of the strip (m). */
  halfW: number;
  /** z of row 0. */
  z0: number;
}

/** The concept's grid for its resolution `res` over [z1, z0], with rows adjusted to divide 800 m. */
export function conceptLattice(res: readonly [number, number], z0: number, z1: number, halfW = 320): Lattice {
  const spacing = PERIOD / Math.round(PERIOD / ((z0 - z1) / res[1]));
  return { nx: res[0], spacing, halfW, z0 };
}

/** Weight of the exact surface at exact height h: 1 at the waterline (< 1 m), 0 inland (≥ 2.2 m). */
export const exactWeight = (h: number): number => 1 - sstep(1.0, 2.2, h);

/** Column offset from the centerline at u ∈ [−1, 1] (concept L262). */
const colX = (u: number, halfW: number): number => halfW * (0.45 * u + 0.55 * u * u * u);

/** Inverse of colX: the u whose column offset is `q · halfW` (monotonic cubic, Newton's method). */
function colU(q: number): number {
  let u = q < -1 ? -1 : q > 1 ? 1 : q;
  for (let k = 0; k < 8; k++) u -= (0.55 * u * u * u + 0.45 * u - q) / (1.65 * u * u + 0.45);
  return u;
}

/** Where (x, z) falls in the lattice: quad (row i, column j), fractions t along z and v across, clamped. */
interface Cell {
  i: number;
  j: number;
  t: number;
  v: number;
}

function locate(L: Lattice, W: WorldProfile, x: number, z: number, out: Cell): Cell {
  const r = (L.z0 - z) / L.spacing;
  const i = Math.floor(r);
  const t = r - i;
  const zi = L.z0 - i * L.spacing;
  // The lattice interpolates the centerline linearly between its rows; every quad is a parallelogram.
  const c = lerp(W.center(zi), W.center(zi - L.spacing), t);
  const u = colU((x - c) / L.halfW);
  let j = Math.floor(((u + 1) / 2) * L.nx);
  j = j < 0 ? 0 : j > L.nx - 1 ? L.nx - 1 : j;
  const x0 = colX((j / L.nx) * 2 - 1, L.halfW);
  const x1 = colX(((j + 1) / L.nx) * 2 - 1, L.halfW);
  out.i = i;
  out.j = j;
  out.t = t;
  out.v = (x - c - x0) / (x1 - x0);
  return out;
}

/**
 * Barycentric weights of the concept's triangle containing (t, v) in quad corners a (i, j), b (i, j+1),
 * c (i+1, j), d (i+1, j+1); the concept splits each quad into (a, b, c) and (b, d, c) (terrain.ts).
 */
function weights(t: number, v: number, out: [number, number, number, number]): void {
  if (t + v <= 1) {
    out[0] = 1 - t - v;
    out[1] = v;
    out[2] = t;
    out[3] = 0;
  } else {
    out[0] = 0;
    out[1] = 1 - t;
    out[2] = 1 - v;
    out[3] = t + v - 1;
  }
}

/** Terrain height the player collides with and sees (GDD §4.2 bank rule): exact at the waterline, the concept's inland. */
export function surfaceHeight(W: WorldProfile, L: Lattice, x: number, z: number): number {
  const hx = W.height(x, z);
  const w = exactWeight(hx);
  if (w >= 1) return hx;
  const cell = locate(L, W, x, z, scratchCell);
  weights(cell.t, cell.v, scratchW);
  let hl = 0;
  for (let k = 0; k < 4; k++) {
    const wk = scratchW[k] ?? 0;
    if (!wk) continue;
    const zi = L.z0 - (cell.i + (k >> 1)) * L.spacing;
    const xi = W.center(zi) + colX(((cell.j + (k & 1)) / L.nx) * 2 - 1, L.halfW);
    hl += wk * W.height(xi, zi);
  }
  return lerp(hl, hx, w);
}
const scratchCell: Cell = { i: 0, j: 0, t: 0, v: 0 };
const scratchW: [number, number, number, number] = [0, 0, 0, 0];

/**
 * Gives a fine terrain strip (built exactly by buildTerrain over [zEnd, z0]) the concept's land: every vertex
 * above the waterline takes the concept mesh's interpolated height, colour and normal, blended by exactWeight.
 */
export function applyConceptLand(fine: BufferGeometry, W: WorldProfile, L: Lattice, zEnd: number): void {
  // One extra lattice row before z0 and after zEnd, so every row used has interior (two-sided) normals.
  const rows = Math.ceil((L.z0 - zEnd) / L.spacing - 1e-9) + 3;
  const lat = buildTerrain(W, L.z0 + L.spacing, L.z0 + L.spacing - rows * L.spacing, L.nx, rows, L.halfW);
  const lp = lat.getAttribute('position').array;
  const lc = lat.getAttribute('color').array;
  const ln = lat.getAttribute('normal').array;
  const fp = fine.getAttribute('position').array;
  const fc = fine.getAttribute('color').array;
  const fn = fine.getAttribute('normal').array;
  const cols = L.nx + 1;
  const cell: Cell = { i: 0, j: 0, t: 0, v: 0 };
  const wt: [number, number, number, number] = [0, 0, 0, 0];
  const n = fp.length / 3;
  for (let p = 0; p < n; p++) {
    const o = p * 3;
    const hx = fp[o + 1] ?? 0;
    const w = exactWeight(hx);
    if (w >= 1) continue;
    locate(L, W, fp[o] ?? 0, fp[o + 2] ?? 0, cell);
    weights(cell.t, cell.v, wt);
    let h = 0;
    let r = 0;
    let g = 0;
    let b = 0;
    let nx = 0;
    let ny = 0;
    let nz = 0;
    for (let k = 0; k < 4; k++) {
      const wk = wt[k] ?? 0;
      if (!wk) continue;
      const q = ((cell.i + 1 + (k >> 1)) * cols + cell.j + (k & 1)) * 3;
      h += wk * (lp[q + 1] ?? 0);
      r += wk * (lc[q] ?? 0);
      g += wk * (lc[q + 1] ?? 0);
      b += wk * (lc[q + 2] ?? 0);
      nx += wk * (ln[q] ?? 0);
      ny += wk * (ln[q + 1] ?? 0);
      nz += wk * (ln[q + 2] ?? 0);
    }
    fp[o + 1] = lerp(h, hx, w);
    fc[o] = lerp(r, fc[o] ?? 0, w);
    fc[o + 1] = lerp(g, fc[o + 1] ?? 0, w);
    fc[o + 2] = lerp(b, fc[o + 2] ?? 0, w);
    nx = lerp(nx, fn[o] ?? 0, w);
    ny = lerp(ny, fn[o + 1] ?? 0, w);
    nz = lerp(nz, fn[o + 2] ?? 0, w);
    const len = Math.hypot(nx, ny, nz) || 1;
    fn[o] = nx / len;
    fn[o + 1] = ny / len;
    fn[o + 2] = nz / len;
  }
  lat.dispose();
  for (const name of ['position', 'color', 'normal']) fine.getAttribute(name).needsUpdate = true;
  fine.computeBoundingSphere();
}
